import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * Any icon component that accepts a `className` — satisfied by both a
 * `lucide-react` icon and the local `@noor/ui` CPanel glyphs (see
 * `cpanel-icons.tsx`). Broader than `LucideIcon` on purpose so either works.
 */
type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * A single row in a secondary/vertical nav list (design ref: the "Мои чаты /
 * Клиенты / Курьеры / Рестораны / Открытые / Решенные" panel). Purely
 * presentational — the caller owns routing and decides which row is `active`.
 *
 * Anatomy: leading lucide icon + label, with an optional `trailing` slot for a
 * count badge or checkmark. The off state reads muted on a transparent
 * background; the active state fills a soft-brand pill.
 *
 * Supports `asChild` so it can wrap a router `<Link>` while keeping @noor/ui
 * router-agnostic.
 */
const sidebarNavItemVariants = cva(
  "flex h-9 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-medium tracking-tight transition-colors duration-150 ease-out outline-none focus-visible:ring-ring/40 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
  {
    variants: {
      active: {
        true: 'bg-sidebar-accent text-sidebar-accent-foreground',
        false:
          'bg-transparent text-sidebar-foreground hover:bg-muted hover:text-foreground [&_svg]:text-muted-foreground',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

type SidebarNavItemProps = Omit<React.ComponentProps<'button'>, 'children'> &
  VariantProps<typeof sidebarNavItemVariants> & {
    /** Leading lucide icon rendered at `size-5`. */
    icon: IconComponent;
    /** The row label. */
    label: React.ReactNode;
    /** Optional trailing content — e.g. a count `Badge` or a checkmark. */
    trailing?: React.ReactNode;
    asChild?: boolean;
  };

function SidebarNavItem({
  className,
  icon: Icon,
  label,
  trailing,
  active = false,
  asChild = false,
  ...props
}: SidebarNavItemProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="sidebar-nav-item"
      data-active={active}
      className={cn(sidebarNavItemVariants({ active }), className)}
      {...props}
    >
      <Icon className="size-5" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing !== undefined ? (
        <span className="ml-auto flex shrink-0 items-center">{trailing}</span>
      ) : null}
    </Comp>
  );
}

export { SidebarNavItem, sidebarNavItemVariants };
export type { SidebarNavItemProps };
